/**
 * Card de plano único, compartilhado entre a página pública (/planos) e a
 * página de billing dentro do app (/billing) — P23C follow-up: antes cada
 * página tinha sua própria cópia do card (preço, badges, botão), divergindo
 * em detalhes (ex: aviso "* Referência visual" só existia em uma delas),
 * o que deixava o visual inconsistente entre as duas telas.
 *
 * Props:
 *   plan            objeto do catálogo (GET /billing/plans)
 *   billingPeriod   'quarterly' | 'annual'
 *   isCurrent       boolean — plano atual do usuário
 *   isUpgrade       boolean
 *   isDowngrade     boolean
 *   popular         boolean — mostra badge "Mais Popular" (usado só na página pública)
 *   loading         boolean — desabilita o botão
 *   notAuthenticated boolean — troca o texto do CTA para "Cadastre-se para Assinar"
 *   purchaseEnabled boolean — false esconde o CTA de compra (App Store
 *                   Guideline 3.1.1: no app nativo sem IAP configurado, o
 *                   plano é exibido como informação, sem oferta de compra)
 *   storePriceLabel string — preço já formatado pela loja (nativo). Tem
 *                   prioridade sobre o preço do catálogo: exibir valor
 *                   diferente do cobrado é causa de rejeição na App Store
 *   requireStorePrice boolean — no nativo, se o preço da loja não chegou,
 *                   não exibe o do catálogo (poderia divergir do cobrado)
 *   onSelect        (plan, isDowngrade) => void
 */
export function PlanCard({ plan, billingPeriod, isCurrent, isUpgrade, isDowngrade, popular, loading, notAuthenticated, purchaseEnabled = true, storePriceLabel, requireStorePrice = false, onSelect }) {
  const priceInfo = plan.prices?.[billingPeriod];
  if (!priceInfo) return null;

  const monthlyPrice = priceInfo / 100 / (billingPeriod === 'quarterly' ? 3 : 12);
  const cycleLabel = billingPeriod === 'quarterly' ? 'Trimestre' : 'Ano';

  let actionButton;
  if (!purchaseEnabled && !isCurrent) {
    // Sem CTA, sem link, sem instrução de "assine no site" — qualquer um dos
    // três reprova na revisão da Apple quando não há IAP.
    actionButton = (
      <button disabled className="w-full font-bold py-3.5 px-4 rounded-xl text-xs bg-surface-container-high text-on-surface-variant border border-outline-variant cursor-default">
        Indisponível neste dispositivo
      </button>
    );
  } else if (isCurrent) {
    actionButton = (
      <button disabled className="w-full font-bold py-3.5 px-4 rounded-xl text-xs bg-primary/20 text-primary border border-primary/40 cursor-default">
        ✓ Seu plano atual
      </button>
    );
  } else if (isDowngrade) {
    actionButton = (
      <button
        onClick={() => onSelect(plan, true)}
        disabled={loading}
        className="w-full font-bold py-3.5 px-4 rounded-xl text-xs bg-surface-container-high border border-outline-variant text-on-surface-variant hover:bg-surface-container transition-colors disabled:opacity-50"
      >
        {loading ? 'Aguarde...' : 'Fazer Downgrade'}
      </button>
    );
  } else {
    actionButton = (
      <button
        onClick={() => onSelect(plan, false)}
        disabled={loading}
        className={`w-full font-bold py-3.5 px-4 rounded-xl text-xs transition-all ${
          popular
            ? 'bg-primary text-on-primary hover:opacity-90 shadow-md'
            : 'bg-surface-container-high hover:bg-surface-container text-on-surface'
        } disabled:opacity-50`}
      >
        {loading ? 'Aguarde...' : isUpgrade ? 'Fazer Upgrade' : notAuthenticated ? 'Cadastre-se para Assinar' : 'Assinar Agora'}
      </button>
    );
  }

  return (
    <div
      className={`relative flex flex-col justify-between rounded-2xl p-8 border transition-all duration-300 ${
        isCurrent
          ? 'border-primary ring-2 ring-primary/30 bg-surface-container-low'
          : popular
          ? 'border-primary bg-surface-container-low shadow-[0_12px_40px_rgba(212,175,55,0.15)] md:-translate-y-2'
          : 'border-outline-variant/30 bg-surface-container-lowest/50 hover:-translate-y-1 hover:shadow-lg'
      }`}
    >
      {isCurrent && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-on-primary px-4 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider shadow-md whitespace-nowrap">
          Plano Atual
        </div>
      )}
      {!isCurrent && popular && (
        <div className="absolute top-0 right-6 -translate-y-1/2 bg-primary text-on-primary px-4 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider shadow-md">
          Mais Popular
        </div>
      )}

      <div>
        <h3 className="font-serif text-xl font-bold text-on-surface mb-2">{plan.name}</h3>
        <p className="text-xs text-on-surface-variant/70 mb-6">{plan.tagline}</p>

        <div className="mb-6">
          {storePriceLabel ? (
            // Preço formatado pela própria loja (moeda e localização do
            // usuário). Exibir valor diferente do que a loja vai cobrar é
            // causa de rejeição — por isso ele tem prioridade sobre o preço
            // do catálogo do backend, que é o de referência da web.
            <>
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-bold tracking-tight text-primary">{storePriceLabel}</span>
              </div>
              <div className="text-xs text-on-surface-variant/60 mt-1">por {cycleLabel.toLowerCase()}</div>
            </>
          ) : requireStorePrice ? (
            // No nativo, sem preço da loja não inventamos um: mostrar o valor
            // do catálogo aqui poderia divergir do cobrado.
            <div className="text-sm text-on-surface-variant/60">Preço indisponível no momento</div>
          ) : (
            <>
              <div className="flex items-baseline gap-1">
                <span className="text-sm font-semibold text-on-surface-variant">R$</span>
                <span className="text-4xl font-bold tracking-tight text-primary">
                  {monthlyPrice.toFixed(2).replace('.', ',')}
                </span>
                <span className="text-xs text-on-surface-variant">/ mês</span>
              </div>
              <div className="text-xs text-on-surface-variant/60 mt-1">
                R$ {(priceInfo / 100).toFixed(2).replace('.', ',')} / {cycleLabel}
              </div>
            </>
          )}
        </div>

        <ul className="space-y-3 mb-8 border-t border-outline-variant/20 pt-6">
          {plan.features.map((feature, idx) => (
            <li key={idx} className="flex items-start gap-2.5 text-xs text-on-surface-variant">
              <span className="material-symbols-outlined text-primary text-sm shrink-0">check_circle</span>
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </div>

      {actionButton}
    </div>
  );
}
